import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthContext";
import { Home } from "./pages/Home";
import { Pledge } from "./pages/Pledge";
import { MyPledge } from "./pages/MyPledge";
import { About } from "./pages/About";
import { GuestbookPage } from "./pages/Guestbook";
import { Photos } from "./pages/Photos";
import { Account } from "./pages/Account";
import { SignIn } from "./pages/SignIn";
import { Admin } from "./pages/Admin";
import { AuthCallback } from "./pages/AuthCallback";
import { Chrome } from "./components/Chrome";

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/signin" state={{ from: loc.pathname }} replace />;
  return children;
}

function RequireAdmin({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/signin" state={{ from: loc.pathname }} replace />;
  if (!user.isAdmin) return <Navigate to="/" replace />;
  return children;
}

// Keep a self-referencing <link rel="canonical"> pointed at the current route.
// The SPA serves identical HTML on every path, which Search Console flagged as
// "Duplicate without user-selected canonical"; a per-route canonical resolves it.
function useCanonical() {
  const loc = useLocation();
  useEffect(() => {
    const base = (import.meta.env.VITE_PUBLIC_BASE_URL as string) || window.location.origin;
    const href = `${base.replace(/\/$/, "")}${loc.pathname}`;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;
  }, [loc.pathname]);
}

export function App() {
  useCanonical();
  return (
    <Chrome>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/guestbook" element={<GuestbookPage />} />
        <Route path="/photos" element={<Photos />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route
          path="/pledge"
          element={
            <RequireAuth>
              <Pledge />
            </RequireAuth>
          }
        />
        <Route
          path="/my-pledge"
          element={
            <RequireAuth>
              <MyPledge />
            </RequireAuth>
          }
        />
        <Route
          path="/account"
          element={
            <RequireAuth>
              <Account />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <Admin />
            </RequireAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Chrome>
  );
}
